import { FunctionComponent, ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "primereact/utils";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { translationKeys } from "../../../../../../models/app-translation-keys";
import { UploadFileListItem } from "../../../../../../models/classes/upload-file-list-item";
import { UploadFile } from "../../../../../../models/classes/upload-file.class";
import { IProgressedFileResult } from "../../../../../../models/interfaces/progressed-file-result.interface";
import { fileSizeToString } from "../../../../ValuePipes/file-size-to-string.function";
import { splitFileName } from "../../../../ValuePipes/split-file-name-type.function";

import "./FileListItem.scss";

import { ActionButton } from "../../../../Buttons";
import { ShowTooltipWhenTextTruncated } from "../../../../ShowTooltipWhenTruncated";

interface IFileListItemProps {
  uploadFileListItem: UploadFileListItem;
  uploadTemplate?: (
    uploadFile: UploadFile,
    setIsFileProcessing: (id: string, isProcessing: boolean) => void,
    downloadFunction: (file: UploadFile, content: IProgressedFileResult) => void,
  ) => ReactNode;
  downloadFunction: (file: UploadFile, content: IProgressedFileResult) => void;
  setIsFileProcessing: (id: string, isProcessing: boolean) => void;
  handleRemoveFile: (file: UploadFile) => void;
}

export const FileListItemComponent: FunctionComponent<IFileListItemProps> = (props: IFileListItemProps) => {
  const { t } = useTranslation();

  const renderUploadTemplateMemo = useMemo(() => {
    return props.uploadTemplate
      ? props.uploadTemplate(props.uploadFileListItem.uploadFile, props.setIsFileProcessing, props.downloadFunction)
      : null;
  }, []);

  return (
    <div
      className={classNames("file-list-item", {
        "has-error": !!props.uploadFileListItem.uploadFile.errors?.length,
      })}
    >
      <div className="file-list-item-header">
        <div className="file-icon">
          <FontAwesomeIcon icon={props.uploadFileListItem.uploadFile.icon} />
        </div>

        <div className="file-info">
          <ShowTooltipWhenTextTruncated
            forceShowTooltip={false}
            tooltipPosition="top"
            tooltipContent={props.uploadFileListItem.uploadFile.file.name}
          >
            <div className="file-name-type">
              <span className="file-name">{splitFileName(props.uploadFileListItem.uploadFile.file, "name")}</span>.
              <span>{splitFileName(props.uploadFileListItem.uploadFile.file, "type")}</span>
            </div>
          </ShowTooltipWhenTextTruncated>

          <span className="file-description">
            {fileSizeToString(props.uploadFileListItem.uploadFile.fileSizeInByte)}
          </span>
        </div>

        <ActionButton
          icon={faXmark}
          hideBackground={true}
          tooltipDisabled={!props.uploadFileListItem.isProgressing}
          tooltip={t(translationKeys.upload.fileInProgress)}
          tooltipPosition="top"
          buttonDisabled={props.uploadFileListItem.isProgressing}
          onClick={() => props.handleRemoveFile(props.uploadFileListItem.uploadFile)}
          customClasses="file-delete-icon"
        />
      </div>

      {!props.uploadFileListItem.uploadFile.errors?.length ? (
        <div className="file-list-item-content">{!!props.uploadTemplate && renderUploadTemplateMemo}</div>
      ) : (
        <div className="file-list-item-errors">
          {props.uploadFileListItem.uploadFile.errors.map((error: string, index: any) => (
            <div key={index}>{t(`upload.error.${error}`)}</div>
          ))}
        </div>
      )}
    </div>
  );
};
